/**
 * The app's URLs, built in one place.
 *
 * Pure string functions, so the router, the links and the tests all agree by construction
 * rather than by four separate template literals that drift. Nothing here reads the DOM or
 * the location — a caller passes the ids in, exactly like the functional core rule the
 * Worker side follows.
 *
 * **No token ever appears in any of these.** An id is a path segment and there is no query
 * string anywhere in the product: the invitation token lives in `sessionStorage`
 * (`invite-token.ts`) and a Clerk JWT travels in an `Authorization` header (`api.ts`). A
 * route that took either would put it into Worker logs, referrers and PostHog's
 * `$current_url` — the leak both of those files are written around.
 */

/** The route patterns, as `react-router` matches them. Exported so the tests name them once. */
export const ROUTE_GROUPS = '/';
export const ROUTE_NEW_GROUP = '/new';
export const ROUTE_GROUP = '/g/:groupId';
export const ROUTE_NEW_MATERIAL = '/g/:groupId/new';
export const ROUTE_MATERIAL = '/g/:groupId/m/:materialId';
export const ROUTE_JOIN = '/join';
/**
 * The invitation email's decline link. Its fragment is held under a key of its own by
 * `invite-token.ts`, which is what keeps a decline token away from the accept (KTD7).
 */
export const ROUTE_DECLINE = '/decline';
/**
 * The Planner's "Check my lesson" link. Its fragment is read by `planner-handoff.ts` before
 * anything mounts and the address bar is pointed at `/`, so the router only ever sees this
 * path when someone types it — and then it simply goes home (KTD4). A hand-off naming a
 * material then opens `materialPath` from `App`, once (planner collab materials KTD6).
 */
export const ROUTE_FROM_PLANNER = '/from-planner';

/** The teacher's groups. */
export const groupsPath = (): string => ROUTE_GROUPS;

/**
 * Starting a group: a destination, not a name field at the bottom of the list.
 *
 * A top-level literal, and it cannot collide with a group's own address however a teacher
 * names a group — every group id sits behind `/g/`, so `/new` is a segment nothing else in
 * this file can ever produce.
 */
export const newGroupPath = (): string => ROUTE_NEW_GROUP;

/** One group's room: its members, its uploads, its materials. */
export const groupPath = (groupId: string): string => `/g/${encodeURIComponent(groupId)}`;

/**
 * Adding a material: a destination, not a form at the bottom of the room.
 *
 * `new` is a literal segment and `:materialId` is not, so `/g/:groupId/new` and
 * `/g/:groupId/m/:materialId` cannot collide however a teacher names a group — ids are the
 * only things interpolated here, and a material's id sits behind `/m/`.
 */
export const newMaterialPath = (groupId: string): string => `${groupPath(groupId)}/new`;

/**
 * One material's own page — the viewer and the conversation about it.
 *
 * Shareable with another member of the group, and useless to anyone else: opening it asks
 * the Worker, which answers the same 404 it answers for every other route (R5, AE1).
 */
export const materialPath = (groupId: string, materialId: string): string =>
  `${groupPath(groupId)}/m/${encodeURIComponent(materialId)}`;
